import {
  SeQuestionDto,
  useGetSourcingEventQuery,
  useSendSeClarificationMessageAsBuyerMutation,
} from "../../../autogen/bff-api";
import { ClarificationReplyForm } from "../../../common/components/communication/ClarificationReplyForm";
import { ClarificationThread } from "../../../common/components/communication/ClarificationThread";
import { useMessageAttachments } from "../../../common/components/communication/useMessageAttachments";
import { Alert, AlertDescription, AlertIcon, Button, Stack, useDisclosure } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useApiError } from "../../../common/errors/useApiError";
import { useReplyDeadlinePassed } from "../../../common/components/communication/useReplyDeadlinePassed";
import { richTextHasContent } from "../../../common/components/communication/communicationUtils";
import { StartClarificationModal } from "./StartClarificationModal";

export const BuyerClarificationThread = ({
  eventId,
  clarification,
  canAskAgain = true,
}: {
  eventId: string;
  clarification: SeQuestionDto;
  canAskAgain?: boolean;
}) => {
  const { t } = useTranslation();
  const askAgain = useDisclosure();
  const displayer = useApiError();
  const [sendMessage, { isLoading }] = useSendSeClarificationMessageAsBuyerMutation();
  const deadlinePassed = useReplyDeadlinePassed(clarification.replyDeadline);
  const [draft, setDraft] = useState("");
  const unsentDraft = richTextHasContent(draft) ? draft : undefined;
  const { data: event } = useGetSourcingEventQuery({ eventId });
  const requestDocuments = (event?.eventData.documents ?? []).map((d) => ({ id: d.id, name: d.name }));
  const attachments = useMessageAttachments({
    eventId,
    uploadEntityType: "SeV2AnswerAttachment",
    pollRole: "buyer",
    questionId: clarification.id,
  });

  const handleSend = async (content: string) => {
    try {
      await sendMessage({
        eventId,
        questionId: clarification.id,
        createSeQuestionMessageRequest: { content, attachments: attachments.toInputs() },
      }).unwrap();
      return true;
    } catch (error) {
      displayer.trigger(error);
      return false;
    }
  };

  const footer = deadlinePassed ? (
    <Stack spacing="3">
      <Alert status="info" rounded="md" fontSize="sm">
        <AlertIcon />
        <AlertDescription>
          {canAskAgain
            ? t("The reply deadline has passed. To ask again, send a new request with a new deadline; this one stays in the record.")
            : t("The reply deadline has passed. This request stays in the record.")}
        </AlertDescription>
      </Alert>
      {canAskAgain && (
        <Button alignSelf="flex-start" size="sm" colorScheme="teal" variant="outline" onClick={askAgain.onOpen}>
          {t("Ask again")}
        </Button>
      )}
      <StartClarificationModal
        eventId={eventId}
        counterparties={[
          { organizationId: clarification.supplierOrganizationId, organizationName: clarification.supplierOrgName },
        ]}
        fixedCounterpartyId={clarification.supplierOrganizationId}
        isOpen={askAgain.isOpen}
        onClose={askAgain.onClose}
        initialContent={unsentDraft}
        key={unsentDraft ?? "fresh"}
      />
    </Stack>
  ) : (
    <ClarificationReplyForm
      attachments={attachments}
      requestDocuments={requestDocuments}
      isSending={isLoading}
      onSend={handleSend}
      onDraftChange={setDraft}
    />
  );

  return <ClarificationThread question={clarification} viewerIsBuyer footer={footer} />;
};
